import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { LuArrowDownUp, LuSave } from "react-icons/lu";
import { GiCrossedBones } from "react-icons/gi";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { useGetPremiumDepositEntryByIdQuery, useSavePremiumDepositEntryMutation } from "../../../../services/redux/api/components";
import { formatForDateInput, formatToIso } from "../../shared/CommonFunction";
import PolicyDropdown from "../../../../utils/PolicyDropdown";

const PremiumDepositEntry = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = Boolean(id);
  const [showPolicyInfo, setShowPolicyInfo] = useState(true);
  const [selectedPolicy, setSelectedPolicy] = useState(location?.state?.row || null);

  // Initial Values (expanded)
  const initialValues = {
    policyId: "",
    policyNumber: "",
    lifeAssured: "",
    product: "",
    groupCode: "",
    commencementDate: "",
    sumAssured: "",
    termPPT: "",
    ppt: "",
    agency: "",
    dueDate: "",
    paymentDate: "",
    premiumAmount: "",
    gstOnPremium: "",
    lateFeeAmount: "",
    gstOnLateFee: "",
    totalPremiumPaid: "",
    PaymentMode: "",
    PaymentDetails: "",
    Notes: "",
    createdBy: "",
    updatedBy: "",
  };

  // Validation Schema
  const validationSchema = Yup.object().shape({
    policyNumber: Yup.string().required("Policy Number is required"),
    dueDate: Yup.date().required("Due Date is required"),
    paymentDate: Yup.date().required("Payment Date is required"),
    premiumAmount: Yup.number().typeError("Must be a number"),
    gstOnPremium: Yup.number().typeError("Must be a number"),
    lateFeeAmount: Yup.number().typeError("Must be a number"),
    gstOnLateFee: Yup.number().typeError("Must be a number"),
    PaymentMode: Yup.string().required("Payment Mode is required"),
    PaymentDetails: Yup.string(),
    Notes: Yup.string(),
  });

  // RTK mutations / queries
  const [savePremiumDeposit] = useSavePremiumDepositEntryMutation();
  const { data: premiumDataResponse, isLoading } = useGetPremiumDepositEntryByIdQuery(id, { skip: !isEditMode });

  // extract server data object
  const premiumData = premiumDataResponse?.data ?? null;

  // Handle Submit
  const handleSubmit = async (values, { resetForm }) => {
    console.log("handleSubmit called with values:", values); // debug log
    const payload = {
      Mode: isEditMode ? "Update" : "Add",
      PremiumDepositID: isEditMode ? Number(id) : null,
      PolicyId: values.policyId ?? null,
      PolicyNumber: values.policyNumber ?? null,
      DueDate: values.dueDate ? formatToIso(values.dueDate) : null,
      PaymentDate: values.paymentDate ? formatToIso(values.paymentDate) : null,
      PremiumAmount: values.premiumAmount ?? 0,
      GSTOnPremium: values.gstOnPremium ?? 0,
      LateFeeAmount: values.lateFeeAmount ?? 0,
      GSTOnLateFee: values.gstOnLateFee ?? 0,
      LifeAssured: values.lifeAssured ?? null,
      Product: values.product ?? null,
      GroupCode: values.groupCode ?? null,
      CommencementDate: values.commencementDate ? formatToIso(values.commencementDate) : null,
      SumAssured: values.sumAssured ?? null,
      TermPPT: values.termPPT ?? null,
      PPT: values.ppt ?? null,
      Agency: values.agency ?? null,
      PaymentMode: values.PaymentMode ?? null,
      PaymentDetails: values.PaymentDetails ?? null,
      Notes: values.Notes ?? null,
      CreatedBy: values.createdBy ?? null,
      UpdatedBy: values.updatedBy ?? null,
    };

    try {
      const response = await savePremiumDeposit(payload).unwrap();
      if (response?.Status === "SUCCESS" || response?.success === true) {
        toast.success(response?.Message || "Premium deposit saved successfully");
        resetForm();
        window.history.back();
      } else {
        toast.error(response?.Message || "Failed to save premium deposit");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.data?.Message || "Something went wrong");
    }
  };

  return (
    <div className="formContainer lg:p-4">
      <div
        className="flex flex-col p-4 lg:mx-5 border rounded bg-white"
        style={{ boxShadow: "0px 0px 12px #cac8c8" }}
      >
        <h5 className="text-brandRed font-bold border-b border-brandRed pb-2 mb-4 px-2">
          {isEditMode ? "Update Premium Deposit Entry" : "Add Premium Deposit Entry"}
        </h5>

        <Formik
          enableReinitialize
          initialValues={{
            policyId: premiumData?.PolicyId ?? initialValues.policyId,
            policyNumber: premiumData?.PolicyNumber ?? initialValues.policyNumber,
            lifeAssured: premiumData?.LifeAssured ?? initialValues.lifeAssured,
            product: premiumData?.Product ?? initialValues.product,
            groupCode: premiumData?.GroupCode ?? initialValues.groupCode,
            commencementDate: formatForDateInput(premiumData?.CommencementDate) || initialValues.commencementDate,
            sumAssured: premiumData?.SumAssured ?? initialValues.sumAssured,
            termPPT: premiumData?.TermPPT ?? premiumData?.Term ?? initialValues.termPPT,
            ppt: premiumData?.PPT ?? initialValues.ppt,
            agency: premiumData?.Agency ?? initialValues.agency,
            dueDate: formatForDateInput(premiumData?.DueDate) || initialValues.dueDate,
            paymentDate: formatForDateInput(premiumData?.PaymentDate) || initialValues.paymentDate,
            premiumAmount: premiumData?.PremiumAmount ?? initialValues.premiumAmount,
            gstOnPremium: premiumData?.GSTOnPremium ?? initialValues.gstOnPremium,
            lateFeeAmount: premiumData?.LateFeeAmount ?? initialValues.lateFeeAmount,
            gstOnLateFee: premiumData?.GSTOnLateFee ?? initialValues.gstOnLateFee,
            totalPremiumPaid: premiumData?.TotalPremiumPaid ?? initialValues.totalPremiumPaid,
            PaymentMode: premiumData?.PaymentMode ?? initialValues.PaymentMode,
            PaymentDetails: premiumData?.PaymentDetails ?? initialValues.PaymentDetails,
            Notes: premiumData?.Notes ?? initialValues.Notes,
            createdBy: initialValues.createdBy,
            updatedBy: initialValues.updatedBy,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, setFieldValue, values, submitForm }) => (
            <Form
              onSubmitCapture={(e) => {
                // Native submit detection
                console.log("Form onSubmitCapture fired", { isSubmitting });
              }}
            >
              {/* Policy Information */}
              <div className="mb-4 row ">
                <h6 className="text-secondary fw-bold mb-3 bg-gray-100 rounded p-2">
                  Policy Information
                </h6>

                <div className="flex flex-col px-3 py-2 col-lg-5 relative">
                
                  <div className="d-flex align-items-center gap-2 col-sm-12 col-md-12">
                    <PolicyDropdown
                      name="policyNumber"
                      onSelect={(policy) => {
                        setSelectedPolicy(policy || null);
                        if (!policy) {
                          setFieldValue("policyId", "");
                          setFieldValue("policyNumber", "");
                          return;
                        }

                        try {
                          if (policy.PolicyId != null) setFieldValue("policyId", policy.PolicyId);
                          if (policy.PolicyNumber != null) setFieldValue("policyNumber", String(policy.PolicyNumber));
                          else if (policy.PolicyNo != null) setFieldValue("policyNumber", String(policy.PolicyNo));

                          // Set Due Date from FUPDate (as requested)
                          if (policy.FUPDate) setFieldValue("dueDate", formatForDateInput(String(policy.FUPDate)));
                          // Default Payment Date to today if policy.PaymentDate is not provided
                          if (policy.PaymentDate) {
                            setFieldValue("paymentDate", formatForDateInput(String(policy.PaymentDate)));
                          } else {
                            const today = new Date().toISOString().split('T')[0];
                            setFieldValue("paymentDate", today);
                          }
                          // Commencement date: support multiple possible property names returned by API
                          const commDate = policy.CommencementDate || policy.CommDate || policy.Commencement || null;
                          if (commDate) setFieldValue("commencementDate", formatForDateInput(String(commDate)));

                          // Populate premium fields from policy.TotalPremiumPaid and related values
                          if (policy.TotalPremiumPaid != null) setFieldValue("premiumAmount", policy.TotalPremiumPaid);
                          if (policy.TotalPremiumPaid != null) setFieldValue("totalPremiumPaid", policy.TotalPremiumPaid);
                          if (policy.GSTOnPremium != null) setFieldValue("gstOnPremium", policy.GSTOnPremium);
                          if (policy.LateFeeAmount != null) setFieldValue("lateFeeAmount", policy.LateFeeAmount);
                          if (policy.GSTOnLateFee != null) setFieldValue("gstOnLateFee", policy.GSTOnLateFee);
                          if (policy.SumAssured != null) setFieldValue("sumAssured", policy.SumAssured);

                          if (policy.LifeAssured != null) setFieldValue("lifeAssured", String(policy.LifeAssured));
                          // Product: prefer human-readable name if available, else fall back to ID/code
                          const prodName = policy.ProductName || policy.Product || policy.ProductId || policy.ProductCode || null;
                          if (prodName != null) setFieldValue("product", String(prodName));
                          if (policy.GroupCode != null) setFieldValue("groupCode", policy.GroupCode);
                          if (policy.TermPPT != null) setFieldValue("termPPT", policy.TermPPT);
                          if (policy.PPT != null) setFieldValue("ppt", policy.PPT);
                          if (policy.Agency != null) setFieldValue("agency", policy.Agency);

                          if (policy.PaymentMode) setFieldValue("PaymentMode", policy.PaymentMode);
                        } catch (err) {
                          console.error("Error mapping policy to form:", err);
                        }
                      }}
                    />

                    {!showPolicyInfo && (
                      <button
                        type="button"
                        onClick={() => setShowPolicyInfo(true)}
                        className="ml-3 mt-5 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm font-medium border border-blue-300 hover:bg-blue-100 transition whitespace-nowrap"
                      >
                        Show Policy Info
                      </button>
                    )}
                  </div>
                </div>

                <AnimatePresence>
                  {showPolicyInfo && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.5, ease: "easeInOut" }}
                      className="relative overflow-hidden bg-white rounded-xl shadow-md p-3 mt-2 border border-gray-200"
                    >
                      <div
                        onClick={() => setShowPolicyInfo(false)}
                        className="absolute -top-3 left-1/2 -translate-x-1/2 w-12 h-10 bg-orange-400 rounded-b-lg cursor-pointer flex items-center justify-center"
                        title="Hide Policy Information"
                      >
                        <span className="text-white text-xs"><LuArrowDownUp size={20} /> </span>
                      </div>

                      <div className="grid grid-cols-4 gap-2 mt-4 p-3">
                        <div>
                          <p className="text-sm font-bold text-gray-400 mb-2">Life Assured</p>
                          <p className="font-semibold">{values.lifeAssured || selectedPolicy?.LifeAssured || premiumData?.LifeAssured || "-"}</p>
                        </div>

                        <div>
                          <p className="text-sm font-bold text-gray-400 mb-2">Product</p>
                          <p className="font-semibold">{values.product || selectedPolicy?.Product || premiumData?.Product || "-"}</p>
                        </div>

                        <div>
                          <p className="text-sm font-bold text-gray-400 mb-2">Group Code</p>
                          <p className="font-semibold">{values.groupCode || selectedPolicy?.GroupCode || premiumData?.GroupCode || "-"}</p>
                        </div>

                        <div>
                          <p className="text-sm font-bold text-gray-400 mb-2">Commencement Date</p>
                          <p className="font-semibold">{values.commencementDate || formatForDateInput(selectedPolicy?.CommencementDate) || formatForDateInput(premiumData?.CommencementDate) || "-"}</p>
                        </div>

                        <div>
                          <p className="text-sm font-bold text-gray-400 mb-2">Sum Assured</p>
                          <p className="font-semibold">{values.sumAssured ?? selectedPolicy?.SumAssured ?? premiumData?.SumAssured ?? "-"}</p>
                        </div>

                        <div>
                          <p className="text-sm font-bold text-gray-400 mb-2">Term/PPT</p>
                          <p className="font-semibold">{(values.termPPT || selectedPolicy?.TermPPT || premiumData?.TermPPT || selectedPolicy?.Term || premiumData?.term) ? `${values.termPPT || selectedPolicy?.TermPPT || premiumData?.TermPPT || selectedPolicy?.Term || premiumData?.term} / ${values.ppt || selectedPolicy?.PPT || premiumData?.PPT || "-"}` : "-"}</p>
                        </div>

                        <div>
                          <p className="text-sm font-bold text-gray-400 mb-2">Payment Mode</p>
                          <p className="font-semibold">{values.PaymentMode || selectedPolicy?.PaymentMode || premiumData?.PaymentMode || "-"}</p>
                        </div>

                        <div>
                          <p className="text-sm font-bold text-gray-400 mb-2">Agency</p>
                          <p className="font-semibold">{values.agency || selectedPolicy?.Agency || premiumData?.Agency || "-"}</p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Premium Information */}
              <div>
                <h6 className="text-secondary fw-bold mb-3 mt-5 bg-gray-100 rounded p-2">
                  Premium Information
                </h6>

                <div className="row g-3 p-3">
                  <div className="col-md-4">
                    <label className="form-label">
                      Due Date <span className="text-danger">*</span>
                    </label>
                    <Field type="date" name="dueDate" className="form-control custom-shadow" />
                    <ErrorMessage name="dueDate" component="div" className="text-brandRed text-sm" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">
                      Payment Date <span className="text-danger">*</span>
                    </label>
                    <Field type="date" name="paymentDate" className="form-control custom-shadow" />
                    <ErrorMessage name="paymentDate" component="div" className="text-brandRed text-sm" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Premium Amount</label>
                    <Field type="text" name="premiumAmount" className="form-control custom-shadow" />
                    <ErrorMessage name="premiumAmount" component="div" className="text-brandRed text-sm" />
                  </div>
                </div>

                <div className="row g-3 px-3">
                  <div className="col-md-4">
                    <label className="form-label">GST On Premium</label>
                    <Field type="text" name="gstOnPremium" className="form-control custom-shadow" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Late Fee Amount</label>
                    <Field type="text" name="lateFeeAmount" className="form-control custom-shadow" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">GST On Late Fee</label>
                    <Field type="text" name="gstOnLateFee" className="form-control custom-shadow" />
                  </div>
                </div>

                <h6 className="text-secondary fw-bold mb-3 mt-5 bg-gray-100 rounded p-2">
                  Total Premium Payment : {values.totalPremiumPaid || premiumData?.TotalPremiumPaid || "—"}
                </h6>

                <div className="row g-3 px-3">
                  <div className="col-md-6">
                    <label className="form-label">
                      Payment Mode <span className="text-danger">*</span>
                    </label>
                    <Field as="select" name="PaymentMode" className="form-control custom-shadow">
                      <option value="">Select Payment Mode</option>
                      <option value="Cash">Cash</option>
                      <option value="Online">Online</option>
                      <option value="Cheque">Cheque</option>
                    </Field>
                    <ErrorMessage name="PaymentMode" component="div" className="text-brandRed text-sm" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Premium Payment Details</label>
                    <Field type="text" name="PaymentDetails" className="form-control custom-shadow" />
                    <ErrorMessage name="PaymentDetails" component="div" className="text-brandRed text-sm" />
                  </div>
                </div>

                <div className="px-3 py-2">
                  <label className="form-label">Payment Notes</label>
                  <Field as="textarea" name="Notes" className="form-control custom-shadow" rows={3} />
                </div>

                <div className="d-flex justify-content-center gap-3 p-3">
                  {/* Submit via Formik.submitForm() to avoid native submit being blocked */}
                  <button
                    type="button"
                    className="saveButton d-flex align-items-center px-4"
                    onClick={() => {
                      console.log("Save clicked -> calling submitForm()", { isSubmitting, values });
                      submitForm();
                    }}
                    style={{ position: "relative", zIndex: 50 }}
                    disabled={isSubmitting}
                  >
                    <LuSave className="me-2" />
                     {isSubmitting
                      ? (isEditMode ? "Updating..." : "Saving...")
                      : (isEditMode ? "Update" : "Add")}
                  </button>

                  <button type="button" className="backButton d-flex align-items-center px-4" onClick={() => window.history.back()}>
                    <GiCrossedBones className="me-2" />
                    Cancel
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default PremiumDepositEntry;
